import { useRef, useEffect, useCallback } from 'react'

const MAX_ENTRIES = 50
const MAX_ARG_LENGTH = 500

function serialiseArg(arg) {
  if (arg === undefined) return 'undefined'
  if (arg === null) return 'null'
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`
  try {
    const str = JSON.stringify(arg)
    return str.length > MAX_ARG_LENGTH ? str.slice(0, MAX_ARG_LENGTH) + '...' : str
  } catch {
    return String(arg).slice(0, MAX_ARG_LENGTH)
  }
}

export function useConsoleCapture() {
  const bufferRef = useRef([])
  const originalsRef = useRef(null)

  useEffect(() => {
    const originals = {
      log: console.log,
      warn: console.warn,
      error: console.error,
    }
    originalsRef.current = originals

    const intercept = (level) => (...args) => {
      originals[level](...args)
      const entry = {
        level,
        timestamp: new Date().toISOString(),
        message: args.map(serialiseArg).join(' '),
      }
      const buf = bufferRef.current
      if (buf.length >= MAX_ENTRIES) buf.shift()
      buf.push(entry)
    }

    console.log = intercept('log')
    console.warn = intercept('warn')
    console.error = intercept('error')

    return () => {
      console.log = originals.log
      console.warn = originals.warn
      console.error = originals.error
    }
  }, [])

  const getRecentLogs = useCallback(() => {
    const buf = bufferRef.current
    if (buf.length === 0) return ''
    return buf
      .map((e) => `[${e.timestamp}] [${e.level.toUpperCase()}] ${e.message}`)
      .join('\n')
  }, [])

  return { getRecentLogs }
}
